/**
 * @name: Product Share
 * @description: Opens the device share sheet, with copy-link fallback
 * @strict-mode: true
 */

/**
 * @extends {HTMLElement}
 */
class ProductShareButton extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-share-trigger]');
    this.status = this.querySelector('[data-share-status]');

    if (!this.trigger) return;

    this.trigger.addEventListener('click', this.handleShare.bind(this));
  }

  /**
   * @returns {string}
   */
  getShareUrl() {
    return this.dataset.shareUrl || window.location.href;
  }

  /**
   * @returns {string}
   */
  getShareTitle() {
    return this.dataset.shareTitle || document.title;
  }

  /**
   * @returns {Promise<void>}
   */
  async handleShare() {
    const shareData = {
      title: this.getShareTitle(),
      url: window.location.href || this.getShareUrl()
    };

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        this.setStatus('');
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }

    await this.copyLink(shareData.url);
  }

  /**
   * @param {string} url
   * @returns {Promise<void>}
   */
  async copyLink(url) {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(url);
      } else {
        this.fallbackCopy(url);
      }

      this.setStatus(this.dataset.copiedText || 'Link copied');
    } catch (error) {
      this.fallbackCopy(url);
      this.setStatus(this.dataset.copiedText || 'Link copied');
    }
  }

  /**
   * @param {string} url
   */
  fallbackCopy(url) {
    const input = document.createElement('input');
    input.value = url;
    input.setAttribute('readonly', '');
    input.style.position = 'absolute';
    input.style.opacity = '0';
    this.appendChild(input);
    input.select();
    document.execCommand('copy');
    input.remove();
  }

  /**
   * @param {string} message
   */
  setStatus(message) {
    if (!this.status) return;

    this.status.textContent = message;
  }
}

if (!customElements.get('product-share-button')) {
  customElements.define('product-share-button', ProductShareButton);
}
